import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  UserPlus, Edit, Trash2, Users, Loader2, AlertCircle,
  Plus, IndianRupee, RefreshCw, Wallet, ArrowUpCircle, History, Minus, Building2, Store
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFranchiseesTab({ franchisees, isLoading, onRefresh }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [kitchens, setKitchens] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [franchiseeFormData, setFranchiseeFormData] = useState({
    email: "", name: "", username: "", password: "",
    address_line1: "", address_line2: "", city: "", state: "", pincode: "", phone: "",
    operations_manager_phone: "",
    assigned_kitchen_id: "", assigned_outlet_id: "", outlet_name: ""
  });

  // Wallet state
  const [isWalletDialogOpen, setIsWalletDialogOpen] = useState(false);
  const [isTransactionsDialogOpen, setIsTransactionsDialogOpen] = useState(false);
  const [selectedFranchisee, setSelectedFranchisee] = useState(null);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [walletFormData, setWalletFormData] = useState({
    amount: "", reference: "", notes: "", type: "credit"
  });

  // Fetch kitchens on mount
  useEffect(() => {
    fetchKitchens();
  }, []);

  // Fetch outlets when kitchen changes
  useEffect(() => {
    if (franchiseeFormData.assigned_kitchen_id) {
      fetchOutlets(franchiseeFormData.assigned_kitchen_id);
    } else {
      setOutlets([]);
    }
  }, [franchiseeFormData.assigned_kitchen_id]);

  const fetchKitchens = async () => {
    try {
      const res = await axios.get(`${API}/ops-extended/kitchens`);
      setKitchens(res.data);
    } catch (error) {
      console.error("Error fetching kitchens:", error);
    }
  };

  const fetchOutlets = async (kitchenId) => {
    try {
      const res = await axios.get(`${API}/ops-extended/outlets?kitchen_id=${kitchenId}`);
      setOutlets(res.data);
    } catch (error) {
      console.error("Error fetching outlets:", error);
    }
  };

  const openAddDialog = () => {
    setEditingUser(null);
    setFranchiseeFormData({ 
      email: "", name: "", username: "", password: "",
      address_line1: "", address_line2: "", city: "", state: "", pincode: "", phone: "",
      operations_manager_phone: "",
      assigned_kitchen_id: "", assigned_outlet_id: "", outlet_name: ""
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (e) => {
    const userId = e.currentTarget.getAttribute('data-user-id');
    const franchisee = franchisees.find(f => f.user_id === userId);
    if (!franchisee) {
      toast.error("Could not find franchisee");
      return;
    }
    setEditingUser(userId);
    setFranchiseeFormData({
      email: franchisee.email, 
      name: franchisee.name,
      username: franchisee.username || "", 
      password: "",
      address_line1: franchisee.address_line1 || "",
      address_line2: franchisee.address_line2 || "",
      city: franchisee.city || "",
      state: franchisee.state || "",
      pincode: franchisee.pincode || "",
      phone: franchisee.phone || "",
      operations_manager_phone: franchisee.operations_manager_phone || "",
      assigned_kitchen_id: franchisee.assigned_kitchen_id || "",
      assigned_outlet_id: franchisee.assigned_outlet_id || "",
      outlet_name: franchisee.outlet_name || ""
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!franchiseeFormData.email || !franchiseeFormData.name) {
      toast.error("Email and name are required");
      return;
    }
    // For new franchisees, require username and password for login
    if (!editingUser && (!franchiseeFormData.username || !franchiseeFormData.password)) {
      toast.error("Username and password are required for new franchisees to enable login");
      return;
    }
    setIsSubmitting(true);
    try {
      if (editingUser) {
        await axios.put(`${API}/admin/franchisees/${editingUser}`, franchiseeFormData);
        toast.success("Franchisee updated");
      } else {
        await axios.post(`${API}/admin/franchisees`, franchiseeFormData);
        toast.success("Franchisee created");
      }
      setIsDialogOpen(false);
      setEditingUser(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save franchisee");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Deactivate this franchisee?")) return;
    try {
      await axios.delete(`${API}/admin/franchisees/${userId}`);
      toast.success("Franchisee deactivated");
      onRefresh();
    } catch (error) {
      toast.error("Failed to deactivate");
    }
  };

  // Wallet functions
  const openWalletDialog = (franchisee, type = "credit") => {
    setSelectedFranchisee(franchisee);
    setWalletFormData({ amount: "", reference: "", notes: "", type });
    setIsWalletDialogOpen(true);
  };

  const openTransactionsDialog = async (franchisee) => {
    setSelectedFranchisee(franchisee);
    try {
      const response = await axios.get(`${API}/admin/franchisees/${franchisee.user_id}/wallet/transactions`);
      setWalletTransactions(response.data);
      setIsTransactionsDialogOpen(true);
    } catch (error) {
      toast.error("Failed to fetch transactions");
    }
  };

  const handleWalletSubmit = async () => {
    if (!walletFormData.amount || parseFloat(walletFormData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!walletFormData.reference) {
      toast.error("Payment reference is required");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const endpoint = walletFormData.type === "refund" 
        ? `${API}/admin/franchisees/${selectedFranchisee.user_id}/wallet/refund`
        : `${API}/admin/franchisees/${selectedFranchisee.user_id}/wallet/credit`;
      
      await axios.post(endpoint, {
        amount: parseFloat(walletFormData.amount),
        reference: walletFormData.reference,
        notes: walletFormData.notes || null
      });
      
      toast.success(walletFormData.type === "refund" ? "Refund processed" : "Wallet credited successfully");
      setIsWalletDialogOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update wallet");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-6">
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Franchisees</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1">{franchisees.length}</p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1 text-green-600">
                  {franchisees.filter(f => f.is_active).length}
                </p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6 flex items-center justify-center">
            <Button onClick={openAddDialog} className="rounded-full" data-testid="add-franchisee-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Franchisee
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-card overflow-hidden" data-testid="franchisees-table-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg lg:text-xl">Franchisees</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0 sm:p-6">
          {isLoading ? (
            <div className="flex justify-center py-8"><div className="spinner"></div></div>
          ) : franchisees.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No franchisees yet</p>
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[700px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Kitchen/Outlet</TableHead>
                    <TableHead>Wallet</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {franchisees.map((f) => (
                    <TableRow key={f.user_id}>
                      <TableCell>
                        <div>
                          <p className="font-semibold text-sm">{f.name}</p>
                          <p className="text-xs text-muted-foreground">{f.username || "-"}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[150px]">{f.email}</p>
                          <p className="text-xs text-muted-foreground">{f.phone || "No phone"}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {f.outlet_name || f.assigned_kitchen_id ? (
                          <div className="flex flex-col gap-1">
                            {f.outlet_name && (
                              <div className="flex items-center gap-1 text-xs">
                                <Store className="w-3 h-3 text-primary" />
                                <span className="font-medium">{f.outlet_name}</span>
                              </div>
                            )}
                            {f.assigned_kitchen_id && (
                              <Badge variant="outline" className="text-xs w-fit">
                                {kitchens.find(k => k.kitchen_id === f.assigned_kitchen_id)?.name || "Kitchen"}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className={`font-heading text-lg flex items-center ${(f.wallet_balance || 0) < 500 ? 'text-yellow-600' : 'text-green-600'}`}>
                            <IndianRupee className="w-3 h-3" />
                            {(f.wallet_balance || 0).toLocaleString('en-IN')}
                          </span>
                          <div className="flex gap-1 mt-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => openWalletDialog(f, "credit")}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => openTransactionsDialog(f)}
                            >
                              <History className="w-3 h-3 mr-1" />
                              History
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`rounded-full text-xs ${f.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                          {f.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          data-user-id={f.user_id}
                          onClick={openEditDialog}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-destructive" 
                          onClick={() => handleDelete(f.user_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Franchisee Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="franchisee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingUser ? "Edit Franchisee" : "Add New Franchisee"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Franchisees can log in with either Google (email) or username/password.
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  data-testid="franchisee-name-input"
                  value={franchiseeFormData.name}
                  onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={franchiseeFormData.phone}
                  onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, phone: e.target.value })}
                  placeholder="+91 9876543210"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email (for Google Login) *</Label>
                <Input
                  data-testid="franchisee-email-input"
                  type="email"
                  value={franchiseeFormData.email}
                  onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Username {editingUser ? "" : "(Required for login)"} <span className={editingUser ? "text-gray-400" : "text-red-500"}>*</span></Label>
                <Input
                  value={franchiseeFormData.username}
                  onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, username: e.target.value })}
                  placeholder="username"
                  required={!editingUser}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Password {editingUser ? "(leave blank to keep current)" : "(Required for login)"} {!editingUser && <span className="text-red-500">*</span>}</Label>
              <Input
                type="password"
                value={franchiseeFormData.password}
                onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, password: e.target.value })}
                placeholder={editingUser ? "Enter new password or leave blank" : "password"}
                required={!editingUser}
              />
            </div>
            
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-3">Delivery Address</h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Address Line 1</Label>
                  <Input
                    value={franchiseeFormData.address_line1}
                    onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, address_line1: e.target.value })}
                    placeholder="Shop/Building name, Street address"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address Line 2</Label>
                  <Input
                    value={franchiseeFormData.address_line2}
                    onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, address_line2: e.target.value })}
                    placeholder="Landmark, Area (optional)"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={franchiseeFormData.city}
                      onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, city: e.target.value })}
                      placeholder="City"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={franchiseeFormData.state}
                      onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, state: e.target.value })}
                      placeholder="State"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Pincode</Label>
                    <Input
                      value={franchiseeFormData.pincode}
                      onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, pincode: e.target.value })}
                      placeholder="400001"
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Kitchen & Outlet Assignment
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Assigned Kitchen *</Label>
                    <Select
                      value={franchiseeFormData.assigned_kitchen_id}
                      onValueChange={(v) => setFranchiseeFormData({ 
                        ...franchiseeFormData, 
                        assigned_kitchen_id: v,
                        assigned_outlet_id: "" // Reset outlet when kitchen changes
                      })}
                    >
                      <SelectTrigger data-testid="kitchen-select">
                        <SelectValue placeholder="Select kitchen" />
                      </SelectTrigger>
                      <SelectContent>
                        {kitchens.map((k) => (
                          <SelectItem key={k.kitchen_id} value={k.kitchen_id}>
                            {k.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Orders will be sent to ops managers of this kitchen
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Assigned Outlet (Optional)</Label>
                    <Select
                      value={franchiseeFormData.assigned_outlet_id}
                      onValueChange={(v) => {
                        const outlet = outlets.find(o => o.outlet_id === v);
                        setFranchiseeFormData({ 
                          ...franchiseeFormData, 
                          assigned_outlet_id: v,
                          outlet_name: outlet?.name || ""
                        });
                      }}
                      disabled={!franchiseeFormData.assigned_kitchen_id}
                    >
                      <SelectTrigger data-testid="outlet-select">
                        <SelectValue placeholder={franchiseeFormData.assigned_kitchen_id ? "Select outlet" : "Select kitchen first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {outlets.map((o) => (
                          <SelectItem key={o.outlet_id} value={o.outlet_id}>
                            {o.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Outlet/Store Name</Label>
                  <Input
                    data-testid="outlet-name-input"
                    value={franchiseeFormData.outlet_name}
                    onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, outlet_name: e.target.value })}
                    placeholder="e.g., Andheri West Store"
                  />
                  <p className="text-xs text-muted-foreground">
                    This name will be shown on orders for identification
                  </p>
                </div>
              </div>
              
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp Order Alerts (Fallback)
                </h3>
                <div className="space-y-2">
                  <Label>Operations Manager Phone</Label>
                  <Input
                    data-testid="operations-manager-phone-input"
                    value={franchiseeFormData.operations_manager_phone}
                    onChange={(e) => setFranchiseeFormData({ ...franchiseeFormData, operations_manager_phone: e.target.value })}
                    placeholder="+91 9876543210"
                  />
                  <p className="text-xs text-muted-foreground">
                    Fallback WhatsApp number if no ops manager is assigned to the selected kitchen
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="rounded-full">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingUser ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wallet Dialog */}
      <Dialog open={isWalletDialogOpen} onOpenChange={setIsWalletDialogOpen}>
        <DialogContent className="max-w-md" data-testid="wallet-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              {walletFormData.type === "refund" ? (
                <><RefreshCw className="w-5 h-5 text-blue-500" /> Process Refund</>
              ) : (
                <><ArrowUpCircle className="w-5 h-5 text-green-500" /> Add Funds to Wallet</>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedFranchisee && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted rounded-xl">
                <p className="font-semibold">{selectedFranchisee.name}</p>
                <p className="text-sm text-muted-foreground">{selectedFranchisee.email}</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Current Balance:</span>
                  <span className="font-heading text-lg text-green-600 flex items-center">
                    <IndianRupee className="w-4 h-4" />
                    {(selectedFranchisee.wallet_balance || 0).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant={walletFormData.type === "credit" ? "default" : "outline"}
                  className="flex-1 rounded-full"
                  onClick={() => setWalletFormData({ ...walletFormData, type: "credit" })}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Payment
                </Button>
                <Button
                  variant={walletFormData.type === "refund" ? "default" : "outline"}
                  className="flex-1 rounded-full"
                  onClick={() => setWalletFormData({ ...walletFormData, type: "refund" })}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refund
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Amount *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={walletFormData.amount}
                    onChange={(e) => setWalletFormData({ ...walletFormData, amount: e.target.value })}
                    placeholder="0.00"
                    className="pl-8 text-lg"
                    data-testid="wallet-amount-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Payment Reference *</Label>
                <Input
                  value={walletFormData.reference}
                  onChange={(e) => setWalletFormData({ ...walletFormData, reference: e.target.value })}
                  placeholder={walletFormData.type === "refund" ? "e.g., Order #XXX cancelled" : "e.g., Cash received, Bank transfer ref"}
                  data-testid="wallet-reference-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={walletFormData.notes}
                  onChange={(e) => setWalletFormData({ ...walletFormData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>

              {walletFormData.amount && parseFloat(walletFormData.amount) > 0 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-700">New Balance After {walletFormData.type === "refund" ? "Refund" : "Credit"}:</span>
                    <span className="font-heading text-xl text-green-700 flex items-center">
                      <IndianRupee className="w-4 h-4" />
                      {((selectedFranchisee.wallet_balance || 0) + parseFloat(walletFormData.amount || 0)).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsWalletDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button 
              onClick={handleWalletSubmit} 
              disabled={isSubmitting} 
              className={`rounded-full ${walletFormData.type === "refund" ? "bg-blue-500 hover:bg-blue-600" : "bg-green-500 hover:bg-green-600"}`}
              data-testid="save-wallet-btn"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {walletFormData.type === "refund" ? "Process Refund" : "Add Funds"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transactions Dialog */}
      <Dialog open={isTransactionsDialogOpen} onOpenChange={setIsTransactionsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh]" data-testid="transactions-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <History className="w-5 h-5 text-blue-500" />
              Wallet Transactions
            </DialogTitle>
          </DialogHeader>
          {selectedFranchisee && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-xl">
                <p className="font-semibold">{selectedFranchisee.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">Current Balance:</span>
                  <span className="font-heading text-lg text-green-600 flex items-center">
                    <IndianRupee className="w-4 h-4" />
                    {(selectedFranchisee.wallet_balance || 0).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {walletTransactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Wallet className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  walletTransactions.map((txn) => (
                    <div key={txn.transaction_id} className="p-3 border rounded-xl">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          {txn.type === "credit" && <ArrowUpCircle className="w-5 h-5 text-green-500" />}
                          {txn.type === "debit" && <Minus className="w-5 h-5 text-red-500" />}
                          {txn.type === "refund" && <RefreshCw className="w-5 h-5 text-blue-500" />}
                          <div>
                            <p className="font-medium capitalize text-sm">{txn.type}</p>
                            <p className="text-xs text-muted-foreground">{new Date(txn.created_at).toLocaleString('en-IN')}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-heading ${txn.type === "debit" ? "text-red-500" : "text-green-500"}`}>
                            {txn.type === "debit" ? "-" : "+"}₹{txn.amount.toLocaleString('en-IN')}
                          </p>
                          <p className="text-xs text-muted-foreground">Bal: ₹{txn.balance_after.toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
                        <p><strong>Reference:</strong> {txn.reference}</p>
                        {txn.notes && <p><strong>Notes:</strong> {txn.notes}</p>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTransactionsDialogOpen(false)} className="rounded-full">Close</Button>
            <Button onClick={() => { setIsTransactionsDialogOpen(false); openWalletDialog(selectedFranchisee, "credit"); }} className="rounded-full bg-green-500 hover:bg-green-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Funds
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
